import React, { useEffect, useState } from "react";
import { Link, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import {
  Mountain, LayoutDashboard, MapPinned, Users2, Hotel, Plane, Bus, CalendarRange,
  CreditCard, FileEdit, Settings, LogOut, DollarSign, TrendingUp, Mail, Save, Plus, Trash2, ExternalLink, CheckCircle2,
  HelpCircle, Globe, ScrollText, Image as ImageIcon, Tag, Upload, X, Inbox,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtDate } from "../lib/date";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { PillButton, Overline } from "../components/primitives";

const cls = "w-full h-11 rounded-xl border border-black/10 px-4 focus:outline-none focus:ring-2 focus:ring-primary text-sm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

function ImageUploadField({ value, onChange, label = "Image", testid = "image" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/admin/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        {value ? (
          <img src={mediaSrc(value)} alt="" className="w-16 h-16 rounded-lg object-cover border border-black/10" data-testid={`${testid}-preview`} />
        ) : (
          <div className="w-16 h-16 rounded-lg border border-dashed border-black/20 grid place-items-center text-foreground/30"><ImageIcon className="w-5 h-5" /></div>
        )}
        <div className="flex flex-wrap gap-2">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
          <PillButton type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload`}>
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : value ? "Replace" : "Upload Image"}
          </PillButton>
          {value && (
            <PillButton type="button" variant="outline" onClick={() => onChange("")} data-testid={`${testid}-remove`}>
              <X className="w-4 h-4" /> Remove
            </PillButton>
          )}
        </div>
      </div>
      <input className={cls} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={`${label} URL (optional alternative)`} data-testid={`${testid}-url`} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-black/10 p-6">
      <Icon className="w-5 h-5 text-primary" />
      <div className="overline mt-3 text-foreground/50">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1">{value}</div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-primary font-display">Loading…</div>;
  return (
    <div className="space-y-6">
      <div><Overline>Overview</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Dashboard</h2></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={DollarSign} label="Total Revenue" value={`$${stats.total_revenue?.toLocaleString()}`} />
        <Stat icon={CalendarRange} label="Total Bookings" value={stats.total_bookings} />
        <Stat icon={CheckCircle2} label="Confirmed" value={stats.confirmed_bookings} />
        <Stat icon={Users2} label="Travelers" value={stats.travelers} />
        <Stat icon={TrendingUp} label="Newsletter Subs" value={stats.newsletter_subscribers} />
        <Stat icon={Mail} label="Unread Messages" value={stats.unread_messages} />
      </div>
    </div>
  );
}

function Retreats() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/retreats").then((res) => setR(res.data[0])).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put(`/admin/retreats/${r.id}`, r); toast.success("Retreat saved!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!r) return <div className="text-primary font-display">Loading…</div>;
  const set = (k, v) => setR({ ...r, [k]: v });
  const setDep = (i, k, v) => setR({ ...r, departures: r.departures.map((d, idx) => idx === i ? { ...d, [k]: v } : d) });
  const addDep = () => setR({ ...r, departures: [...(r.departures || []), { id: `dep-${Date.now()}`, start_date: "", end_date: "", label: "New Retreat Date", capacity: r.capacity || 20, spots_booked: 0, min_travelers: r.min_travelers || 6 }] });
  const removeDep = (i) => setR({ ...r, departures: r.departures.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Manage</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Retreat</h2></div>
        <PillButton onClick={save} data-testid="retreat-save"><Save className="w-4 h-4" /> Save</PillButton></div>
      <div className="rounded-2xl bg-white border border-black/10 p-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className="text-sm font-medium">Name</label><input className={cls} value={r.name} onChange={(e) => set("name", e.target.value)} data-testid="retreat-name-input" /></div>
        <div><label className="text-sm font-medium">Price ($)</label><input type="number" className={cls} value={r.price} onChange={(e) => set("price", parseFloat(e.target.value))} data-testid="retreat-price-input" /></div>
        <div><label className="text-sm font-medium">Deposit ($)</label><input type="number" className={cls} value={r.deposit_amount} onChange={(e) => set("deposit_amount", parseFloat(e.target.value))} /></div>
        <div><label className="text-sm font-medium">Installments</label><input type="number" className={cls} value={r.installment_count} onChange={(e) => set("installment_count", parseInt(e.target.value))} /></div>
        <div><label className="text-sm font-medium">Capacity</label><input type="number" className={cls} value={r.capacity} onChange={(e) => set("capacity", parseInt(e.target.value))} /></div>
        <div><label className="text-sm font-medium">Spots booked</label><input type="number" className={cls} value={r.spots_booked} onChange={(e) => set("spots_booked", parseInt(e.target.value))} /></div>
        <div><label className="text-sm font-medium">Start date</label><input type="date" className={cls} value={r.start_date?.slice(0, 10)} onChange={(e) => set("start_date", new Date(e.target.value).toISOString())} /></div>
        <div><label className="text-sm font-medium">End date</label><input type="date" className={cls} value={r.end_date?.slice(0, 10)} onChange={(e) => set("end_date", new Date(e.target.value).toISOString())} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Summary</label><textarea rows={2} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={r.summary} onChange={(e) => set("summary", e.target.value)} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Description</label><textarea rows={3} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={r.description || ""} onChange={(e) => set("description", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Duration (days)</label><input type="number" className={cls} value={r.duration_days || 6} onChange={(e) => set("duration_days", parseInt(e.target.value))} /></div>
        <div><label className="text-sm font-medium">Fitness level</label><input className={cls} value={r.fitness_level || ""} onChange={(e) => set("fitness_level", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Minimum travelers</label><input type="number" className={cls} value={r.min_travelers || 6} onChange={(e) => set("min_travelers", parseInt(e.target.value))} data-testid="retreat-min-travelers" /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Minimum travelers policy</label><textarea rows={2} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={r.min_travelers_policy || ""} onChange={(e) => set("min_travelers_policy", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Flights — departure airport</label><input className={cls} value={r.flights?.departure_airport || ""} onChange={(e) => set("flights", { ...r.flights, departure_airport: e.target.value })} /></div>
        <div><label className="text-sm font-medium">Flights — arrival</label><input className={cls} value={r.flights?.arrival || ""} onChange={(e) => set("flights", { ...r.flights, arrival: e.target.value })} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Flights — disclosure</label><textarea rows={2} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={r.flights?.disclosure || ""} onChange={(e) => set("flights", { ...r.flights, disclosure: e.target.value })} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Meals (comma separated)</label><input className={cls} value={(r.meals || []).join(", ")} onChange={(e) => set("meals", e.target.value.split(",").map((s) => s.trim()))} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Transportation</label><textarea rows={2} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={r.transportation || ""} onChange={(e) => set("transportation", e.target.value)} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Included (comma separated)</label><textarea rows={2} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={(r.included || []).join(", ")} onChange={(e) => set("included", e.target.value.split(",").map((s) => s.trim()))} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Not included (comma separated)</label><textarea rows={2} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={(r.not_included || []).join(", ")} onChange={(e) => set("not_included", e.target.value.split(",").map((s) => s.trim()))} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Cancellation policy</label><textarea rows={3} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={r.cancellation_policy || ""} onChange={(e) => set("cancellation_policy", e.target.value)} /></div>
        <div className="sm:col-span-2 flex items-center gap-2"><input type="checkbox" checked={r.published} onChange={(e) => set("published", e.target.checked)} className="w-5 h-5 accent-[#D95D39]" /> <span className="text-sm">Published</span></div>
      </div>

      <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-4" data-testid="departures-editor">
        <div className="flex items-center justify-between">
          <div><h3 className="font-display font-bold text-lg">Retreat Dates (Departures)</h3><p className="text-xs text-foreground/50 mt-1">Each date has its own independent capacity. Changes apply when you click Save above.</p></div>
          <PillButton variant="outline" onClick={addDep} data-testid="add-departure"><Plus className="w-4 h-4" /> Add date</PillButton>
        </div>
        {(r.departures || []).map((d, i) => (
          <div key={d.id} className="rounded-xl border border-black/10 p-4 grid sm:grid-cols-2 gap-3" data-testid={`departure-editor-${i}`}>
            <div className="sm:col-span-2 flex items-center justify-between">
              <span className="text-xs text-foreground/40 font-mono">{d.id}</span>
              <button onClick={() => removeDep(i)} className="text-foreground/40 hover:text-destructive" data-testid={`departure-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="sm:col-span-2"><label className="text-sm font-medium">Label</label><input className={cls} value={d.label} onChange={(e) => setDep(i, "label", e.target.value)} data-testid={`departure-label-${i}`} /></div>
            <div><label className="text-sm font-medium">Start date</label><input type="date" className={cls} value={(d.start_date || "").slice(0, 10)} onChange={(e) => setDep(i, "start_date", e.target.value)} data-testid={`departure-start-${i}`} /></div>
            <div><label className="text-sm font-medium">End date</label><input type="date" className={cls} value={(d.end_date || "").slice(0, 10)} onChange={(e) => setDep(i, "end_date", e.target.value)} data-testid={`departure-end-${i}`} /></div>
            <div><label className="text-sm font-medium">Capacity</label><input type="number" className={cls} value={d.capacity} onChange={(e) => setDep(i, "capacity", parseInt(e.target.value))} /></div>
            <div><label className="text-sm font-medium">Spots booked</label><input type="number" className={cls} value={d.spots_booked} onChange={(e) => setDep(i, "spots_booked", parseInt(e.target.value))} /></div>
            <div><label className="text-sm font-medium">Minimum travelers</label><input type="number" className={cls} value={d.min_travelers} onChange={(e) => setDep(i, "min_travelers", parseInt(e.target.value))} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Founders() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/founders").then((r) => setList(r.data)).catch(() => {}); }, []);
  const save = async (f) => { await api.put(`/admin/founders/${f.id}`, f); toast.success("Saved!"); };
  const upd = (i, k, v) => setList(list.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  return (
    <div className="space-y-6 max-w-3xl">
      <div><Overline>Coaches & Founders</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Founders</h2></div>
      {list.map((f, i) => (
        <div key={f.id} className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
          <div className="flex gap-4">
            <img src={f.photo} alt="" className="w-20 h-20 rounded-xl object-cover" />
            <div className="flex-1 space-y-2">
              <input className={cls} value={f.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Name" />
              <input className={cls} value={f.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Title" />
            </div>
          </div>
          <ImageUploadField value={f.photo} onChange={(v) => upd(i, "photo", v)} label="Photo" testid={`founder-photo-${i}`} />
          <textarea rows={3} className="w-full rounded-xl border border-black/10 p-4 text-sm" value={f.bio} onChange={(e) => upd(i, "bio", e.target.value)} placeholder="Bio" />
          <input className={cls} value={(f.certifications || []).join(", ")} onChange={(e) => upd(i, "certifications", e.target.value.split(",").map((s) => s.trim()))} placeholder="Certifications (comma separated)" />
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={cls} value={f.socials?.instagram || ""} onChange={(e) => upd(i, "socials", { ...f.socials, instagram: e.target.value })} placeholder="Instagram URL" />
            <input className={cls} value={f.socials?.facebook || ""} onChange={(e) => upd(i, "socials", { ...f.socials, facebook: e.target.value })} placeholder="Facebook URL" />
          </div>
          <PillButton onClick={() => save(f)} data-testid={`founder-save-${i}`}><Save className="w-4 h-4" /> Save {f.name.split(" ")[0]}</PillButton>
        </div>
      ))}
    </div>
  );
}

function CrudList({ title, endpoint, fields, testid }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});
  const load = () => api.get(`/admin/${endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { await api.post(`/admin/${endpoint}`, draft); setDraft({}); load(); toast.success("Added!"); };
  const del = async (id) => { await api.delete(`/admin/${endpoint}/${id}`); load(); toast.success("Deleted"); };
  return (
    <div className="space-y-6 max-w-3xl">
      <div><Overline>Manage</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">{title}</h2></div>
      <div className="rounded-2xl bg-white border border-black/10 p-6">
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.map((f) => <input key={f.key} className={cls} placeholder={f.label} value={draft[f.key] || ""} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} data-testid={`${testid}-${f.key}`} />)}
        </div>
        <PillButton onClick={add} className="mt-4" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /> Add</PillButton>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl bg-white border border-black/10 p-4 flex items-center justify-between">
            <div className="text-sm">{fields.map((f) => it[f.key]).filter(Boolean).join(" · ")}</div>
            <button onClick={() => del(it.id)} className="text-foreground/40 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-foreground/50">No entries yet.</div>}
      </div>
    </div>
  );
}

function Itinerary() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/retreats").then((res) => setR(res.data[0])).catch(() => {}); }, []);
  const save = async () => { await api.put(`/admin/itinerary/${r.id}`, r.itinerary); toast.success("Itinerary saved!"); };
  const upd = (i, k, v) => setR({ ...r, itinerary: r.itinerary.map((d, idx) => idx === i ? { ...d, [k]: v } : d) });
  const move = (i, dir) => {
    const arr = [...r.itinerary]; const j = i + dir; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; arr.forEach((d, idx) => d.day = idx + 1); setR({ ...r, itinerary: arr });
  };
  if (!r) return <div className="text-primary font-display">Loading…</div>;
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Day by day</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Itinerary</h2></div>
        <PillButton onClick={save} data-testid="itinerary-save"><Save className="w-4 h-4" /> Save</PillButton></div>
      {r.itinerary.map((d, i) => (
        <div key={i} className="rounded-2xl bg-white border border-black/10 p-5 space-y-2">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-primary">Day {d.day}</span>
            <div className="ml-auto flex gap-1">
              <button onClick={() => move(i, -1)} className="text-foreground/40 hover:text-primary text-xs px-2">↑</button>
              <button onClick={() => move(i, 1)} className="text-foreground/40 hover:text-primary text-xs px-2">↓</button>
            </div>
          </div>
          <input className={cls} value={d.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Title" />
          <input className={cls} value={d.location} onChange={(e) => upd(i, "location", e.target.value)} placeholder="Location" />
          <input className={cls} value={(d.activities || []).join(", ")} onChange={(e) => upd(i, "activities", e.target.value.split(",").map((s) => s.trim()))} placeholder="Activities (comma separated)" />
        </div>
      ))}
    </div>
  );
}

function Travelers() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/admin/travelers").then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
  }, []);
  const depLabels = [...new Set(bookings.map((b) => b.departure_label).filter(Boolean))];
  const shown = filter === "all" ? bookings : bookings.filter((b) => b.departure_label === filter);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><Overline>People</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Travelers & Bookings</h2></div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-black/10 px-3 py-2 text-sm bg-white" data-testid="travelers-date-filter">
          <option value="all">All retreat dates</option>
          {depLabels.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      <div className="rounded-2xl bg-white border border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60"><tr><th className="text-left p-4">Traveler</th><th className="text-left p-4">Retreat date</th><th className="text-left p-4">Status</th><th className="text-left p-4">Paid</th><th className="text-left p-4">Balance</th></tr></thead>
          <tbody>
            {shown.map((b) => (
              <tr key={b.id} className="border-t border-black/5" data-testid={`traveler-row-${b.id}`}>
                <td className="p-4">{b.traveler?.full_legal_name}<div className="text-xs text-foreground/40">{b.traveler?.email}</div></td>
                <td className="p-4">{b.departure_start ? `${fmtDate(b.departure_start)} – ${fmtDate(b.departure_end)}` : "—"}<div className="text-xs text-foreground/40">{b.departure_label || b.retreat_name}</div></td>
                <td className="p-4 capitalize">{b.status?.replace(/_/g, " ")}</td>
                <td className="p-4">${b.amount_paid?.toLocaleString()}</td>
                <td className="p-4">${b.amount_remaining?.toLocaleString()}</td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td className="p-4 text-foreground/50" colSpan={5}>No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="text-sm text-foreground/50">{rows.length} registered travelers</div>
    </div>
  );
}

function Payments() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/payments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refund = async (id) => { await api.post(`/admin/payments/${id}/refund`); toast.success("Refunded"); load(); };
  return (
    <div className="space-y-6">
      <div><Overline>Money</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Payments</h2></div>
      <div className="rounded-2xl bg-white border border-black/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60"><tr><th className="text-left p-4">Payment</th><th className="text-left p-4">Amount</th><th className="text-left p-4">Status</th><th className="text-left p-4">Mode</th><th className="text-left p-4">Date</th><th className="p-4"></th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-black/5">
                <td className="p-4 font-mono text-xs">{p.id.slice(0, 8)}</td>
                <td className="p-4">${p.amount?.toLocaleString()}</td>
                <td className="p-4 capitalize">{p.status}</td>
                <td className="p-4"><span className={`text-xs rounded-full px-2 py-0.5 ${p.processor === "paypal" ? "bg-blue-100 text-blue-700" : p.mode === "quickbooks" ? "bg-green-100 text-green-700" : "bg-secondary"}`}>{p.processor === "paypal" ? "PayPal" : p.mode === "quickbooks" ? "QuickBooks" : (p.mode || "-")}</span>{(p.paypal_capture_id || p.charge_id) && <div className="text-[10px] text-foreground/40 mt-1 font-mono">{p.paypal_capture_id || p.charge_id}</div>}</td>
                <td className="p-4 text-foreground/50">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="p-4">{p.status !== "refunded" && <button onClick={() => refund(p.id)} className="text-xs text-primary hover:underline" data-testid={`refund-${p.id}`}>Refund</button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td className="p-4 text-foreground/50" colSpan={6}>No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CMS() {
  const [content, setContent] = useState({});
  const [key, setKey] = useState("home");
  useEffect(() => { api.get("/content").then((r) => setContent(r.data)).catch(() => {}); }, []);
  const save = async () => { await api.put(`/admin/content/${key}`, content[key]); toast.success("Content published!"); };
  const val = content[key] || {};
  const setVal = (k, v) => setContent({ ...content, [key]: { ...val, [k]: v } });
  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Website</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Content editor</h2></div>
        <PillButton onClick={save} data-testid="cms-save"><Save className="w-4 h-4" /> Publish</PillButton></div>
      <div className="flex gap-2 flex-wrap">
        {["home", "about"].map((k) => <button key={k} onClick={() => setKey(k)} className={`rounded-full px-4 py-2 text-sm capitalize ${key === k ? "bg-primary text-white" : "bg-white border border-black/10"}`}>{k}</button>)}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
        {Object.entries(val).filter(([, v]) => typeof v === "string").map(([k, v]) => (
          <div key={k}>
            <label className="text-sm font-medium capitalize">{k.replace(/_/g, " ")}</label>
            {v.length > 80
              ? <textarea rows={3} className="mt-1.5 w-full rounded-xl border border-black/10 p-4 text-sm" value={v} onChange={(e) => setVal(k, e.target.value)} data-testid={`cms-${k}`} />
              : <input className={`${cls} mt-1.5`} value={v} onChange={(e) => setVal(k, e.target.value)} data-testid={`cms-${k}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminSettings() {
  const [s, setS] = useState({});
  const [qb, setQb] = useState(null);
  const [pp, setPp] = useState(null);
  const [ppTest, setPpTest] = useState({ running: false, result: null });
  const loadQb = () => api.get("/quickbooks/status").then((r) => setQb(r.data)).catch(() => {});
  const loadPp = () => api.get("/paypal/status").then((r) => setPp(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    loadQb();
    loadPp();
  }, []);
  const runPaypalTest = async () => {
    setPpTest({ running: true, result: null });
    try {
      const { data: order } = await api.post("/admin/paypal/test-order");
      const { data: cap } = await api.post("/admin/paypal/test-capture", { order_id: order.order_id });
      setPpTest({ running: false, result: { ok: cap.status === "COMPLETED", order_id: order.order_id, capture_id: cap.capture_id, status: cap.status } });
      if (cap.status === "COMPLETED") toast.success("PayPal $1 Sandbox test captured successfully!");
      else toast.error(`Test capture status: ${cap.status || "unknown"}`);
    } catch (e) {
      setPpTest({ running: false, result: { ok: false, error: formatApiError(e.response?.data?.detail) } });
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved!"); };
  const connect = async () => {
    try { const { data } = await api.get("/quickbooks/connect"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnect = async () => {
    try { await api.post("/quickbooks/disconnect"); toast.success("QuickBooks disconnected."); loadQb(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const badge = qb && (qb.needs_reconnect ? "reconnect required" : qb.mode);
  const badgeCls = qb && (qb.needs_reconnect ? "bg-red-100 text-red-700" : qb.connected ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700");
  return (
    <div className="space-y-6 max-w-2xl">
      <div><Overline>Configuration</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Settings</h2></div>
      <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-4">
        <h3 className="font-display font-bold">Email sender</h3>
        <div><label className="text-sm font-medium">Sender name</label><input className={cls} value={s.sender_name || ""} onChange={(e) => setS({ ...s, sender_name: e.target.value })} data-testid="settings-sender-name" /></div>
        <div><label className="text-sm font-medium">Reply-to email</label><input className={cls} value={s.sender_email || ""} onChange={(e) => setS({ ...s, sender_email: e.target.value })} data-testid="settings-sender-email" />
          <p className="mt-1 text-xs text-foreground/50">Change this later to a branded domain email without editing code.</p></div>
        <PillButton onClick={save} data-testid="settings-save"><Save className="w-4 h-4" /> Save</PillButton>
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3">
        <h3 className="font-display font-bold">QuickBooks Payments</h3>
        {qb && (
          <div className="text-sm">
            <div className="flex items-center gap-2">Status: <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${badgeCls}`} data-testid="qb-status-badge">{badge}</span>
              <span className="text-xs text-foreground/40">({qb.env})</span></div>

            {!qb.configured && <p className="mt-2 text-foreground/60">Add QuickBooks credentials (QB_CLIENT_ID, QB_CLIENT_SECRET, QB_REDIRECT_URI, QB_WEBHOOK_VERIFIER_TOKEN) to the backend Secrets to enable live payments. Until then, payments run in secure test mode.</p>}

            {qb.configured && qb.needs_reconnect && (
              <div className="mt-2 rounded-xl bg-red-50 border border-red-200 p-3 text-red-700" data-testid="qb-reconnect-alert">
                Your QuickBooks authorization has expired or was revoked. Payments are paused until you reconnect.
              </div>
            )}

            {qb.configured && qb.connected && !qb.needs_reconnect && (
              <p className="mt-2 text-green-700">Connected · Realm {qb.realm_id}</p>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              {qb.configured && !qb.connected && (
                <PillButton onClick={connect} data-testid="qb-connect"><ExternalLink className="w-4 h-4" /> Connect QuickBooks</PillButton>
              )}
              {qb.configured && qb.connected && !qb.needs_reconnect && (
                <>
                  <PillButton variant="outline" onClick={connect} data-testid="qb-reconnect"><ExternalLink className="w-4 h-4" /> Reconnect</PillButton>
                  <PillButton variant="outline" onClick={disconnect} data-testid="qb-disconnect"><Trash2 className="w-4 h-4" /> Disconnect</PillButton>
                </>
              )}
              {qb.configured && qb.needs_reconnect && (
                <PillButton onClick={connect} data-testid="qb-reconnect"><ExternalLink className="w-4 h-4" /> Reconnect QuickBooks</PillButton>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="rounded-2xl bg-white border border-black/10 p-6 space-y-3" data-testid="paypal-panel">
        <h3 className="font-display font-bold">PayPal (Sandbox)</h3>
        {pp && (
          <div className="text-sm">
            <div className="flex items-center gap-2">Status:
              <span className={`rounded-full px-2 py-0.5 text-xs ${pp.configured ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid="paypal-status-badge">
                {pp.configured ? "configured" : "not configured"}</span>
              <span className="text-xs text-foreground/40">({pp.env})</span>
            </div>
            {!pp.configured && <p className="mt-2 text-foreground/60">Add PayPal credentials (PAYPAL_CLIENT_ID, PAYPAL_CLIENT_SECRET, PAYPAL_WEBHOOK_ID) to the backend Secrets to enable payments.</p>}
            {pp.configured && pp.env === "live" && (
              <p className="mt-2 text-foreground/60" data-testid="paypal-live-note">PayPal is in <strong>Live</strong> mode. The $1 test is disabled here to prevent accidental real charges. Verify with a real customer booking.</p>
            )}
            {pp.configured && pp.env === "sandbox" && (
              <div className="mt-3 space-y-3">
                <p className="text-foreground/60">Run a real <strong>$1.00 Sandbox</strong> charge to confirm your live connection end-to-end. This uses Sandbox money only — no real funds move.</p>
                <PillButton onClick={runPaypalTest} disabled={ppTest.running} data-testid="paypal-run-test">
                  <CreditCard className="w-4 h-4" /> {ppTest.running ? "Running…" : "Run $1 Sandbox Test"}
                </PillButton>
                {ppTest.result && (
                  <div className={`rounded-xl border p-3 ${ppTest.result.ok ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="paypal-test-result">
                    {ppTest.result.ok ? (
                      <>✓ Sandbox test passed · Status {ppTest.result.status}
                        <div className="text-[10px] font-mono mt-1 text-foreground/50">order {ppTest.result.order_id} · capture {ppTest.result.capture_id}</div></>
                    ) : (
                      <>✕ {ppTest.result.error || `Test capture status: ${ppTest.result.status}`}</>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FAQsEditor() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}); }, []);
  const upd = (i, k, v) => setFaqs(faqs.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const add = () => setFaqs([...faqs, { id: "", category: "General", q: "", a: "" }]);
  const del = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));
  const save = async () => { await api.put("/admin/faqs", faqs); toast.success("FAQs saved!"); };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Content</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">FAQ editor</h2></div>
        <div className="flex gap-2"><PillButton variant="outline" onClick={add} data-testid="faq-add"><Plus className="w-4 h-4" /> Add</PillButton><PillButton onClick={save} data-testid="faq-save"><Save className="w-4 h-4" /> Save</PillButton></div></div>
      {faqs.map((f, i) => (
        <div key={i} className="rounded-2xl bg-white border border-black/10 p-5 space-y-2">
          <div className="flex gap-2"><input className={`${cls} w-40`} value={f.category} onChange={(e) => upd(i, "category", e.target.value)} placeholder="Category" />
            <input className={cls} value={f.q} onChange={(e) => upd(i, "q", e.target.value)} placeholder="Question" />
            <button onClick={() => del(i)} className="text-foreground/40 hover:text-destructive px-2"><Trash2 className="w-4 h-4" /></button></div>
          <textarea rows={2} className="w-full rounded-xl border border-black/10 p-3 text-sm" value={f.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Answer" />
        </div>
      ))}
    </div>
  );
}

function WhyEditor() {
  const [data, setData] = useState({ sections: [], safety: "" });
  useEffect(() => { api.get("/content/why_el_salvador").then((r) => setData(r.data)).catch(() => {}); }, []);
  const upd = (i, k, v) => setData({ ...data, sections: data.sections.map((s, idx) => idx === i ? { ...s, [k]: v } : s) });
  const add = () => setData({ ...data, sections: [...data.sections, { title: "", body: "", image: "" }] });
  const del = (i) => setData({ ...data, sections: data.sections.filter((_, idx) => idx !== i) });
  const save = async () => { await api.put("/admin/content/why_el_salvador", data); toast.success("Saved!"); };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Content</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Why El Salvador</h2></div>
        <div className="flex gap-2"><PillButton variant="outline" onClick={add}><Plus className="w-4 h-4" /> Section</PillButton><PillButton onClick={save} data-testid="why-save"><Save className="w-4 h-4" /> Save</PillButton></div></div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-2">
          <div><label className="text-sm font-medium">Heading</label><input className={cls} value={data.heading || ""} onChange={(e) => setData({ ...data, heading: e.target.value })} data-testid="why-heading" /></div>
          <div><label className="text-sm font-medium">Subheading</label><input className={cls} value={data.subheading || ""} onChange={(e) => setData({ ...data, subheading: e.target.value })} /></div>
        </div>
        <div><label className="text-sm font-medium">Intro (separate paragraphs with a blank line)</label><textarea rows={6} className="mt-1.5 w-full rounded-xl border border-black/10 p-3 text-sm" value={data.intro || ""} onChange={(e) => setData({ ...data, intro: e.target.value })} data-testid="why-intro" /></div>
        <div><label className="text-sm font-medium">Bullets heading</label><input className={cls} value={data.bullets_heading || ""} onChange={(e) => setData({ ...data, bullets_heading: e.target.value })} /></div>
        <div><label className="text-sm font-medium">Bullets (one per line)</label><textarea rows={7} className="mt-1.5 w-full rounded-xl border border-black/10 p-3 text-sm" value={(data.bullets || []).join("\n")} onChange={(e) => setData({ ...data, bullets: e.target.value.split("\n").filter((x) => x.trim()) })} data-testid="why-bullets" /></div>
        <div><label className="text-sm font-medium">Final callout (one line per row)</label><textarea rows={2} className="mt-1.5 w-full rounded-xl border border-black/10 p-3 text-sm" value={data.callout || ""} onChange={(e) => setData({ ...data, callout: e.target.value })} data-testid="why-callout" /></div>
      </div>
      <div className="text-sm font-medium text-foreground/60 pt-2">Image cards (existing imagery)</div>
      {data.sections?.map((s, i) => (
        <div key={i} className="rounded-2xl bg-white border border-black/10 p-5 space-y-2">
          <div className="flex gap-2"><input className={cls} value={s.title} onChange={(e) => upd(i, "title", e.target.value)} placeholder="Title" />
            <button onClick={() => del(i)} className="text-foreground/40 hover:text-destructive px-2"><Trash2 className="w-4 h-4" /></button></div>
          <textarea rows={2} className="w-full rounded-xl border border-black/10 p-3 text-sm" value={s.body} onChange={(e) => upd(i, "body", e.target.value)} placeholder="Body" />
          <ImageUploadField value={s.image} onChange={(v) => upd(i, "image", v)} label="Image" testid={`why-image-${i}`} />
        </div>
      ))}
      <div className="rounded-2xl bg-white border border-black/10 p-5"><label className="text-sm font-medium">Safety & travel prep</label>
        <textarea rows={3} className="mt-1.5 w-full rounded-xl border border-black/10 p-3 text-sm" value={data.safety || ""} onChange={(e) => setData({ ...data, safety: e.target.value })} /></div>
    </div>
  );
}

function LegalEditor() {
  const [data, setData] = useState({});
  useEffect(() => { api.get("/content/legal").then((r) => setData(r.data)).catch(() => {}); }, []);
  const save = async () => { await api.put("/admin/content/legal", data); toast.success("Legal pages saved!"); };
  const keys = [["privacy", "Privacy Policy"], ["terms", "Terms & Conditions"], ["cancellation", "Cancellation & Refunds"], ["waiver", "Travel Waiver"], ["insurance", "Insurance Acknowledgment"]];
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Content</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Legal pages</h2></div>
        <PillButton onClick={save} data-testid="legal-save"><Save className="w-4 h-4" /> Save</PillButton></div>
      {keys.map(([k, label]) => (
        <div key={k} className="rounded-2xl bg-white border border-black/10 p-5"><label className="text-sm font-medium">{label}</label>
          <textarea rows={6} className="mt-1.5 w-full rounded-xl border border-black/10 p-3 text-sm font-mono" value={data[k] || ""} onChange={(e) => setData({ ...data, [k]: e.target.value })} data-testid={`legal-${k}`} /></div>
      ))}
    </div>
  );
}

function GalleryEditor() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/retreats").then((res) => setR(res.data[0])).catch(() => {}); }, []);
  const save = async () => { await api.put(`/admin/retreats/${r.id}`, r); toast.success("Gallery saved!"); };
  if (!r) return <div className="text-primary font-display">Loading…</div>;
  const upd = (i, v) => setR({ ...r, gallery: r.gallery.map((g, idx) => idx === i ? v : g) });
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between"><div><Overline>Photos</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Gallery</h2></div>
        <div className="flex gap-2"><PillButton variant="outline" onClick={() => setR({ ...r, gallery: [...r.gallery, ""] })}><Plus className="w-4 h-4" /> Add</PillButton><PillButton onClick={save} data-testid="gallery-save"><Save className="w-4 h-4" /> Save</PillButton></div></div>
      <div className="rounded-2xl bg-white border border-black/10 p-5"><label className="text-sm font-medium">Hero image</label>
        <div className="mt-1.5"><ImageUploadField value={r.hero_image} onChange={(v) => setR({ ...r, hero_image: v })} label="Hero image" testid="gallery-hero" /></div></div>
      {r.gallery?.map((g, i) => (
        <div key={i} className="rounded-xl bg-white border border-black/10 p-3 flex items-start gap-3">
          <div className="flex-1"><ImageUploadField value={g} onChange={(v) => upd(i, v)} label="Photo" testid={`gallery-item-${i}`} /></div>
          <button onClick={() => setR({ ...r, gallery: r.gallery.filter((_, idx) => idx !== i) })} className="text-foreground/40 hover:text-destructive pt-1" data-testid={`gallery-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function PromoCodes() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ code: "", discount_type: "amount", amount: "", active: true });
  const load = () => api.get("/admin/promo").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { await api.post("/admin/promo", draft); setDraft({ code: "", discount_type: "amount", amount: "", active: true }); load(); toast.success("Promo added!"); };
  const del = async (id) => { await api.delete(`/admin/promo/${id}`); load(); };
  return (
    <div className="space-y-4 max-w-3xl">
      <div><Overline>Marketing</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Promo codes</h2></div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 grid sm:grid-cols-4 gap-3 items-end">
        <input className={cls} placeholder="CODE" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} data-testid="promo-code" />
        <select className={cls} value={draft.discount_type} onChange={(e) => setDraft({ ...draft, discount_type: e.target.value })}><option value="amount">$ amount</option><option value="percent">% percent</option></select>
        <input className={cls} type="number" placeholder="Value" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="promo-amount" />
        <PillButton onClick={add} data-testid="promo-add"><Plus className="w-4 h-4" /> Add</PillButton>
      </div>
      <div className="space-y-2">
        {items.map((p) => (
          <div key={p.id} className="rounded-xl bg-white border border-black/10 p-4 flex items-center justify-between text-sm">
            <span><b>{p.code}</b> · {p.discount_type === "percent" ? `${p.amount}%` : `$${p.amount}`} · {p.active ? "active" : "inactive"}</span>
            <button onClick={() => del(p.id)} className="text-foreground/40 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-foreground/50">No promo codes yet.</div>}
      </div>
    </div>
  );
}

function ContactInfo() {
  const [data, setData] = useState({});
  useEffect(() => { api.get("/content/contact").then((r) => setData(r.data)).catch(() => {}); }, []);
  const save = async () => { await api.put("/admin/content/contact", data); toast.success("Contact info saved!"); };
  const set = (k, v) => setData({ ...data, [k]: v });
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between"><div><Overline>Content</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Contact info & messages</h2></div>
        <PillButton onClick={save} data-testid="contactinfo-save"><Save className="w-4 h-4" /> Save</PillButton></div>
      <div className="rounded-2xl bg-white border border-black/10 p-5 grid sm:grid-cols-2 gap-3">
        <div><label className="text-sm font-medium">Email</label><input className={cls} value={data.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Phone</label><input className={cls} value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Instagram</label><input className={cls} value={data.instagram || ""} onChange={(e) => set("instagram", e.target.value)} /></div>
        <div><label className="text-sm font-medium">Facebook</label><input className={cls} value={data.facebook || ""} onChange={(e) => set("facebook", e.target.value)} /></div>
        <div className="sm:col-span-2"><label className="text-sm font-medium">Support note</label><input className={cls} value={data.support || ""} onChange={(e) => set("support", e.target.value)} /></div>
      </div>
      <Messages />
    </div>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/admin/messages").then((r) => setMsgs(r.data)).catch(() => {}); }, []);
  return (
    <div className="rounded-2xl bg-white border border-black/10 p-5">
      <h3 className="font-display font-bold">Inbox ({msgs.length})</h3>
      <div className="mt-3 space-y-2">
        {msgs.map((m) => (
          <div key={m.id} className="border-b border-black/5 py-2 text-sm">
            <div className="font-medium">{m.subject || "(no subject)"} — {m.name}</div>
            <div className="text-xs text-foreground/50">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
            <div className="text-foreground/70 mt-1">{m.message}</div>
          </div>
        ))}
        {msgs.length === 0 && <div className="text-sm text-foreground/50">No messages yet.</div>}
      </div>
    </div>
  );
}

function ContactLeads() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/admin/leads/${id}`, { status }); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const badge = { new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700", booked: "bg-green-100 text-green-700", closed: "bg-secondary text-foreground/60" };
  return (
    <div className="space-y-6">
      <div><Overline>Inquiries</Overline><h2 className="mt-2 font-display font-extrabold text-2xl">Contact Leads</h2></div>
      {leads.length === 0 && <p className="text-foreground/50 text-sm" data-testid="leads-empty">No leads yet.</p>}
      <div className="space-y-3" data-testid="contact-leads-list">
        {leads.map((l) => (
          <div key={l.id} className="rounded-2xl bg-white border border-black/10 p-5 space-y-3" data-testid={`lead-row-${l.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-display font-bold">{l.full_name}</div>
              <span className={`text-xs rounded-full px-2 py-0.5 capitalize ${badge[l.status] || ""}`} data-testid={`lead-badge-${l.id}`}>{l.status}</span>
            </div>
            <div className="text-sm text-foreground/70 grid sm:grid-cols-2 gap-x-6 gap-y-1">
              <div><b>Email:</b> {l.email}</div>
              <div><b>Phone:</b> {l.phone}</div>
              <div><b>Traveling from:</b> {l.city_state}</div>
              <div><b>Prefers:</b> <span className="capitalize">{l.preferred_contact}</span></div>
              <div className="sm:col-span-2"><b>Questions/comments:</b> {l.message || "—"}</div>
              <div><b>Consent:</b> {l.consent ? "Yes" : "No"}</div>
              <div><b>Submitted:</b> {new Date(l.created_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <label className="text-xs text-foreground/50">Status:</label>
              <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="rounded-lg border border-black/10 px-2 py-1.5 text-sm bg-white" data-testid={`lead-status-${l.id}`}>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="booked">Booked</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/retreat", label: "Retreat", icon: MapPinned },
  { to: "/admin/itinerary", label: "Itinerary", icon: CalendarRange },
  { to: "/admin/founders", label: "Founders", icon: Users2 },
  { to: "/admin/hotels", label: "Hotels", icon: Hotel },
  { to: "/admin/flights", label: "Flights", icon: Plane },
  { to: "/admin/transportation", label: "Transport", icon: Bus },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/why", label: "Why El Salvador", icon: Globe },
  { to: "/admin/legal", label: "Legal", icon: ScrollText },
  { to: "/admin/promo", label: "Promo Codes", icon: Tag },
  { to: "/admin/travelers", label: "Travelers", icon: Users2 },
  { to: "/admin/payments", label: "Payments", icon: CreditCard },
  { to: "/admin/cms", label: "Website CMS", icon: FileEdit },
  { to: "/admin/contact", label: "Contact", icon: Mail },
  { to: "/admin/leads", label: "Contact Leads", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="glass-nav bg-white/80 border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-display font-extrabold"><Mountain className="w-5 h-5 text-primary" /> Admin · La Vida</button>
          <div className="flex items-center gap-4"><span className="text-sm text-foreground/60 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} className="text-foreground/60 hover:text-primary" data-testid="admin-logout"><LogOut className="w-5 h-5" /></button></div>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-[220px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 h-max">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2">
            {nav.map((n) => {
              const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
              return (
                <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().replace(" ", "-")}`}
                  className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${active ? "bg-primary text-white" : "hover:bg-white text-foreground/70"}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </Link>
              );
            })}
          </nav>
        </aside>
        <div>
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="retreat" element={<Retreats />} />
            <Route path="itinerary" element={<Itinerary />} />
            <Route path="founders" element={<Founders />} />
            <Route path="hotels" element={<CrudList title="Hotels" endpoint="hotels" testid="hotel" fields={[{ key: "name", label: "Hotel name" }, { key: "address", label: "Address" }, { key: "description", label: "Description" }, { key: "room_info", label: "Room info (shared/private)" }, { key: "upgrade_note", label: "Private room upgrade note" }, { key: "upgrade_price", label: "Upgrade price" }]} />} />
            <Route path="flights" element={<CrudList title="Flights" endpoint="flights" testid="flight" fields={[{ key: "airline", label: "Airline" }, { key: "flight_number", label: "Flight #" }, { key: "departure_city", label: "Departure city" }, { key: "departure_time", label: "Departure time" }, { key: "arrival_time", label: "Arrival time" }, { key: "status", label: "Status" }]} />} />
            <Route path="transportation" element={<CrudList title="Transportation" endpoint="transportation" testid="transport" fields={[{ key: "type", label: "Type (pickup/shuttle)" }, { key: "driver", label: "Driver" }, { key: "schedule", label: "Schedule" }, { key: "meeting_point", label: "Meeting point" }]} />} />
            <Route path="gallery" element={<GalleryEditor />} />
            <Route path="faqs" element={<FAQsEditor />} />
            <Route path="why" element={<WhyEditor />} />
            <Route path="legal" element={<LegalEditor />} />
            <Route path="promo" element={<PromoCodes />} />
            <Route path="travelers" element={<Travelers />} />
            <Route path="payments" element={<Payments />} />
            <Route path="cms" element={<CMS />} />
            <Route path="contact" element={<ContactInfo />} />
            <Route path="leads" element={<ContactLeads />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
